import gsap from 'gsap'

/**
 * Pastel colors for transition hearts (matching floating hearts)
 */
const PASTEL_COLORS = [
  '#D8B4E2', // Light pastel violet
  '#E8B4D8', // Light pastel lavender-pink
  '#FFB6C1', // Light pink
  '#FFC8DD', // Lighter pink
  '#B8E0F6', // Baby blue
  '#A2D2FF', // Soft sky blue
  '#FFDAB9', // Peach puff
  '#E6E6FA', // Lavender
  '#F0FFF0', // Honeydew
  '#FFE4E1', // Misty rose
]

/**
 * Slightly darker pastel colors for rectangle backgrounds
 * These are subtle variations of the heart colors, just a tiny bit darker
 */
const BLOCK_COLORS = [
  '#C9A5D3', // Slightly darker violet
  '#D9A5C9', // Slightly darker lavender-pink
  '#F0A7B2', // Slightly darker pink
  '#F0B9CE', // Slightly darker lighter pink
  '#A9D1E7', // Slightly darker baby blue
  '#93C3F0', // Slightly darker sky blue
  '#F0CBAA', // Slightly darker peach
  '#D7D7EB', // Slightly darker lavender
  '#E1F0E1', // Slightly darker honeydew
  '#F0D5D2', // Slightly darker misty rose
]

/**
 * Creates an SVG heart element
 */
function createHeartSVG(color: string): string {
  return `
    <svg viewBox="0 0 100 100" fill="${color}" style="width: 60%; height: 60%; position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);">
      <path d="M50 88.9C47.8 88.9 45.7 88.1 44.1 86.6C32.9 76.1 22.4 66.3 13.5 55.8C4.1 44.7 0 35.1 0 25.4C0 11.4 11.4 0 25.4 0C34.1 0 42.3 4.1 47.5 10.8L50 14.1L52.5 10.8C57.7 4.1 65.9 0 74.6 0C88.6 0 100 11.4 100 25.4C100 35.1 95.9 44.7 86.5 55.8C77.6 66.3 67.1 76.1 55.9 86.6C54.3 88.1 52.2 88.9 50 88.9Z"/>
    </svg>
  `
}

/**
 * Gets a random pastel color for hearts
 */
function getRandomPastelColor(): string {
  return PASTEL_COLORS[Math.floor(Math.random() * PASTEL_COLORS.length)]
}

/**
 * Gets a random block background color (slightly darker pastels)
 */
function getRandomBlockColor(): string {
  return BLOCK_COLORS[Math.floor(Math.random() * BLOCK_COLORS.length)]
}

/**
 * Gets the number of columns based on viewport width (matching CSS breakpoints)
 */
function getColumnCount(): number {
  const width = window.innerWidth
  if (width <= 479) return 4
  if (width <= 767) return 6
  return 8
}

/**
 * Adjusts the transition grid to fill the viewport with square blocks
 */
export function adjustGrid(): Promise<void> {
  return new Promise((resolve) => {
    const transition = document.getElementById('transition')
    if (!transition) {
      resolve()
      return
    }

    // Get column count based on viewport (matching CSS media queries)
    const columns = getColumnCount()

    const blockSize = window.innerWidth / columns
    const rowsNeeded = Math.ceil(window.innerHeight / blockSize)

    // Update grid styles
    transition.style.gridTemplateRows = `repeat(${rowsNeeded}, ${blockSize}px)`

    // Calculate the total number of blocks needed
    const totalBlocks = columns * rowsNeeded

    // Clear existing blocks
    transition.innerHTML = ''

    // Generate blocks dynamically with hearts inside
    for (let i = 0; i < totalBlocks; i++) {
      const block = document.createElement('div')
      block.classList.add('transition-block')
      
      // Set random background color for the block
      const blockColor = getRandomBlockColor()
      block.style.backgroundColor = blockColor
      block.style.outlineColor = blockColor
      
      // Add a heart SVG with random pastel color
      const heartColor = getRandomPastelColor()
      block.innerHTML = createHeartSVG(heartColor)
      
      transition.appendChild(block)
    }

    // Resolve the Promise after grid creation is complete
    resolve()
  })
}

/**
 * Triggers the pixelated page transition effect
 * Blocks animate in with random stagger, then animate out to reveal content
 * @param onScreenCovered - Callback to execute when screen is fully covered (before reveal)
 */
export function triggerPixelTransition(onScreenCovered?: () => void): Promise<void> {
  return new Promise((resolve) => {
    const transition = document.getElementById('transition')
    if (!transition) {
      resolve()
      return
    }

    // Show the transition container first so we can set up the grid
    transition.style.display = 'grid'
    transition.style.background = 'transparent'

    // Now adjust the grid (element is visible, so calculations work)
    adjustGrid().then(() => {
      // Set initial state - blocks are invisible
      gsap.set('.transition-block', { autoAlpha: 0 })

      // Create timeline for the transition
      const timeline = gsap.timeline({
        onComplete: () => {
          // Hide the transition container when done
          gsap.set('.transition', { display: 'none' })
          resolve()
        },
        defaults: {
          ease: 'linear',
        },
      })

      // Animate blocks IN (appear with random stagger)
      timeline.to('.transition-block', {
        autoAlpha: 1,
        duration: 0.001,
        stagger: { amount: 0.5, from: 'random' },
        onComplete: () => {
          // Screen is now fully covered - execute callback to switch content
          if (onScreenCovered) {
            onScreenCovered()
          }
        },
      })

      // Hold for a moment (user sees solid color screen)
      timeline.to({}, { duration: 0.3 })

      // Animate blocks OUT (disappear with random stagger to reveal new section)
      timeline.to('.transition-block', {
        autoAlpha: 0,
        duration: 0.1,
        stagger: { amount: 0.75, from: 'random' },
      })
    })
  })
}

/**
 * Initialize the transition module
 * Sets up the grid and adds resize listener
 */
export function initTransition(): void {
  // Initial grid setup
  adjustGrid()

  // Handle window resize
  window.addEventListener('resize', () => {
    adjustGrid()
  })
}
